import * as R from 'ramda'

import { VALID_FILE_TYPES } from '~/modules/media'
import bytesToMegabytes from '~/utils/bytesToMegabytes'

const MAX_FILE_SIZE = 262144000 // 250MB
const GENERIC_ERROR = {
  name: 'Error',
  type: 'Something went wrong. Try another file, or try refreshing your browser',
}

const isValidFileSize = ({ size }, maxSize) => size < maxSize
const isValidMIMEType = ({ type }) => VALID_FILE_TYPES.includes(type)

// validates and uploads a single file. This function must return a resolved
// promise, so that Promise.all can process it correctly (Promise.all cannot
// handle rejected promises effectively). This function returns a resolved
// "error" object on error, or a resolved "null" on success.
const handleFile = (file, serviceFkey, uploadMutation, handleUploadedMedia) => {
  if (!isValidMIMEType(file)) {
    return Promise.resolve({
      name: file.name,
      type: 'Invalid file type (Only images/videos, PDF, Word, Excel and txt accepted)',
    })
  }
  if (!isValidFileSize(file, MAX_FILE_SIZE)) {
    return Promise.resolve({
      name: file.name,
      type: `File is too large: ${bytesToMegabytes(file.size)}mb
        (Maximum file size ${bytesToMegabytes(MAX_FILE_SIZE)}mb)`,
    })
  }

  return uploadMutation({ variables: { file, serviceFkey } })
    .then((result) => {
      const { id, coverUrl } = R.path(['data', 'uploadMedia'], result)

      handleUploadedMedia && handleUploadedMedia({ coverUrl, id, mimeType: file.type })

      return Promise.resolve()
    })
    .catch(() => {
      return Promise.resolve({
        name: file.name,
        type: 'Media failed to upload',
      })
    })
}

// Fires off multiple `handleFile`s asychronously inside a Promise.all -
// Promise.all only handles "resolved" promises well - as such all errors
// returned from `handleFile` are actually "resolved" promises. This function
// will return a list of errors, or an empty list
const uploadMultipleFiles = (files, serviceFkey, uploadMutation, handleUploadedMedia) => {
  // Promise.all ensures that all returned errors update state "errors" at
  // once. Else, async calls can race condition one another
  return Promise.all(files.map((file) => handleFile(file, serviceFkey, uploadMutation, handleUploadedMedia)))
    .then((uploadErrors) =>
      // Filter out "nulls" to only return errors
      uploadErrors.filter((err) => err)
    )
    .catch(() =>
      // Return generic error if critical error occurs
      [GENERIC_ERROR]
    )
}

export default uploadMultipleFiles
